import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Award, CheckCircle2, ArrowRight, Star, ShieldCheck, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import AppointmentCTA from "../components/AppointmentCTA";
import FAQSection from "../components/FAQSection";
import { clinic, images, trustPoints, conditions, diagnostics, stats, homeFaqs, reviews } from "../data/mock";
import { getWhatsAppLink } from "../lib/whatsapp";

const reasons = [
  { title: "15+ Years of Focused Liver Care", desc: "Deep, specialized experience in diagnosing and treating the full spectrum of liver diseases." },
  { title: "Advanced FibroScan Diagnostics", desc: "Painless, precise assessment of liver fat and fibrosis for accurate, early diagnosis." },
  { title: "Proven Reversal & Recovery", desc: "Thousands of patients successfully treated for fatty liver, hepatitis and jaundice." },
  { title: "Honest, Personalized Treatment", desc: "Clear explanations and realistic, tailored plans — never unnecessary tests or medicines." },
];

const BestLiverDoctor = () => {
  return (
    <div>
      <SEO path="/best-liver-doctor-in-varanasi" faqs={homeFaqs} reviews={reviews} />
      <PageHero breadcrumb="Best Liver Doctor in Varanasi"
        title="Best Liver Doctor in Varanasi"
        subtitle={`${clinic.doctorName} (${clinic.qualifications}) — a leading liver specialist & hepatologist trusted by thousands of patients across Varanasi.`} />

      {/* INTRO */}
      <section className="py-16 sm:py-20">
        <div className="container-app grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative flex justify-center">
            <div className="absolute inset-0 bg-gradient-to-tr from-sky-400 to-sky-200 rounded-[2.5rem] rotate-3 scale-95 opacity-40 blur-xl" />
            <div className="relative bg-gradient-to-b from-sky-100 to-white rounded-[2.5rem] p-4 shadow-2xl shadow-sky-200/50">
              <img src={images.doctor} alt={`${clinic.doctorName} - Best Liver Doctor in Varanasi`} className="w-full max-w-sm rounded-[2rem]" />
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl px-5 py-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-sky-500 grid place-items-center text-white"><Award size={22} /></div>
                <div>
                  <div className="font-heading font-bold text-slate-900 leading-tight">{clinic.experience} Years</div>
                  <div className="text-xs text-slate-500">Liver Care Experience</div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <SectionHeading center={false} eyebrow="Why He's Trusted" title="Varanasi's Trusted Liver Specialist" />
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>When searching for the <strong className="text-slate-800">best liver doctor in Varanasi</strong>, patients look for accuracy, honesty and results. {clinic.doctorName} brings all three — combining {clinic.experience} years of specialized experience with advanced diagnostics like FibroScan and high-definition endoscopy.</p>
              <p>As a qualified Gastroenterologist and Hepatologist ({clinic.qualifications}), he treats everything from early fatty liver to complex cirrhosis and hepatitis, with a strong focus on early detection and reversible outcomes.</p>
              <p>Every patient receives a clear diagnosis, a realistic treatment plan and compassionate, ongoing care at {clinic.clinicName}.</p>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-8 h-12 font-semibold shadow-lg shadow-sky-500/30 w-full sm:w-auto">
                  <MessageCircle size={20} className="mr-2" /> Book Appointment
                </Button>
              </a>
              <Link to="/gastro-liver-specialist-dr-indresh-dixit/">
                <Button size="lg" variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-50 rounded-full px-8 h-12 font-semibold w-full sm:w-auto">
                  Full Profile <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-sky-600">
        <div className="container-app grid grid-cols-2 lg:grid-cols-4 divide-x divide-sky-500/40">
          {stats.map((s) => (
            <div key={s.label} className="py-8 text-center">
              <div className="font-heading text-3xl sm:text-4xl font-bold text-white">{s.value}</div>
              <div className="text-sky-100 text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY BEST */}
      <section className="py-16 sm:py-20">
        <div className="container-app">
          <SectionHeading eyebrow="What Makes Him the Best" title="Why Patients Choose Dr. Indresh Dixit"
            subtitle="The qualities that make him one of the most recommended liver doctors in Varanasi." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {reasons.map((t, i) => (
              <div key={i} className="group bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 grid place-items-center mb-5 group-hover:bg-sky-500 group-hover:text-white transition-colors">
                  <ShieldCheck size={24} />
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{t.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONDITIONS */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Conditions Treated" title="Complete Liver & Digestive Care"
            subtitle="Expert treatment for every liver condition, all under one roof." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {conditions.map((c, i) => {
              const Icon = Icons[c.icon] || Icons.Activity;
              return (
                <Link key={i} to={c.link} className="group bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-xl hover:border-sky-200 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 text-white grid place-items-center mb-5 shadow-lg shadow-sky-500/30">
                    <Icon size={24} />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{c.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-4">{c.desc}</p>
                  <span className="inline-flex items-center gap-1 text-sky-600 font-semibold text-sm group-hover:gap-2 transition-all">Learn More <ArrowRight size={16} /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* DIAGNOSTICS */}
      <section className="py-16 sm:py-20">
        <div className="container-app">
          <SectionHeading eyebrow="Advanced Diagnostics" title="Accurate Liver Diagnosis" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {diagnostics.map((d, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-lg transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-heading text-sky-500 font-bold text-xl">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-heading font-semibold text-lg text-slate-900">{d.title}</h3>
                </div>
                <p className="text-slate-500 text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEW STRIP */}
      <section className="py-16 sm:py-20 bg-slate-900">
        <div className="container-app text-center">
          <div className="flex justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => <Star key={i} size={22} className="fill-amber-400 text-amber-400" />)}
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white max-w-2xl mx-auto">Rated 4.9/5 by 180+ Happy Patients Across Varanasi</h2>
          <p className="text-slate-300 mt-4 max-w-2xl mx-auto">Join the thousands of patients who trusted Dr. Indresh Dixit with their liver health and recovered with confidence.</p>
        </div>
      </section>

      <FAQSection faqs={homeFaqs} title="Best Liver Doctor in Varanasi — FAQs" />
      <AppointmentCTA title="Consult the Best Liver Doctor in Varanasi Today" />
    </div>
  );
};

export default BestLiverDoctor;
